// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { AddURLLinkParams, urlLinkSchema } from '@/url-link/api/types';
import { z } from 'zod';

const addPurchaseOrderURLLinkResponseSchema = z
  .object({
    data: urlLinkSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, ...data }));

type AddPurchaseOrderURLLinkResponse = z.infer<
  typeof addPurchaseOrderURLLinkResponseSchema
>;

/**
 * Adds a URL link and associates it with a specific purchase order.
 * Calls the backend endpoint POST /v1/purchase-order/{purchaseOrderKey}/url-link.
 *
 * @param params - The parameters for creating the purchase order URL link
 * @returns Promise containing the created URL link details
 */
export async function addPurchaseOrderURLLink(
  params: AddURLLinkParams
): Promise<AddPurchaseOrderURLLinkResponse> {
  const apiUrl = `/v1/purchase-order/${params.entityKey}/url-link`;
  const payload = {
    name: params.name,
    url: params.url,
  };

  const response = await api.post(apiUrl, payload);

  return parseResponse(addPurchaseOrderURLLinkResponseSchema, response.data, {
    schemaName: 'AddPurchaseOrderURLLink',
    endpoint: apiUrl,
  });
}
